import { useTranslation } from "react-i18next";
import { LocalizedText } from "../../types/LocalizedText";
import {
  PurchaseReturnProduct,
  PurchaseReturnUser,
  PurchaseReturnVariation,
} from "../../types/PurchaseReturn";

export const getLocalizedName = (
  name: LocalizedText | string | undefined | null,
  language: string,
) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name[language as keyof LocalizedText] || name.en || name.ar || "";
};

export const getPersonName = (
  user?: Omit<PurchaseReturnUser, "id"> | null,
  fallback = "--",
) => {
  if (!user) return fallback;
  const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return fullName || user.phone || user.email || fallback;
};

// Mirrors getVariationDisplayName on the purchase order page: prefer attribute
// values, otherwise strip the parent SKU prefix from the variation SKU.
export const getItemDisplay = (
  product: PurchaseReturnProduct | null | undefined,
  variation: PurchaseReturnVariation | null | undefined,
  language: string,
) => {
  const baseProduct = product || variation?.product || null;
  const name = getLocalizedName(baseProduct?.name, language);

  let variationLabel = "";
  const attributeValues = Object.values(variation?.attributes || {}).filter(
    Boolean,
  );
  if (attributeValues.length > 0) {
    variationLabel = attributeValues.join(", ");
  } else if (variation?.sku) {
    const productSku = baseProduct?.sku || "";
    variationLabel =
      productSku && variation.sku.startsWith(`${productSku}-`)
        ? variation.sku.slice(productSku.length + 1).replace(/-/g, " ")
        : variation.sku;
  }

  return {
    name,
    variationLabel,
    sku: variation?.sku || baseProduct?.sku || "",
  };
};

export const formatDate = (dateString?: string | null, withTime = false) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

export const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Same palette as the purchase order StatusBadge
export const PURCHASE_RETURN_STATUS_CONFIG: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  pending: { color: "text-orange-700", bg: "bg-orange-100", label: "Pending" },
  approved: { color: "text-white", bg: "bg-[#0d223f]", label: "Approved" },
  completed: {
    color: "text-green-700",
    bg: "bg-green-100",
    label: "Completed",
  },
  rejected: { color: "text-red-700", bg: "bg-red-100", label: "Rejected" },
  cancelled: { color: "text-gray-700", bg: "bg-gray-200", label: "Cancelled" },
};

export const PurchaseReturnStatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const config =
    PURCHASE_RETURN_STATUS_CONFIG[status] ||
    PURCHASE_RETURN_STATUS_CONFIG.pending;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.color} ${config.bg}`}
    >
      {t(config.label)}
    </span>
  );
};

const REFUND_STATUS_CONFIG: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  not_applicable: {
    color: "text-gray-600",
    bg: "bg-gray-100",
    label: "Not Applicable",
  },
  pending: {
    color: "text-yellow-800",
    bg: "bg-yellow-100",
    label: "Refund Pending",
  },
  received: {
    color: "text-green-700",
    bg: "bg-green-100",
    label: "Refund Received",
  },
};

export const RefundStatusBadge = ({ status }: { status?: string | null }) => {
  const { t } = useTranslation();
  const config =
    REFUND_STATUS_CONFIG[status || "not_applicable"] ||
    REFUND_STATUS_CONFIG.not_applicable;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config.color} ${config.bg}`}
    >
      {t(config.label)}
    </span>
  );
};
